import { elementFromEventTarget, getDocument } from '@/lib/utils';

/**
 * DOM selectors for rich-text / block editor UI rendered in portals (format toolbar,
 * block toolbars). Used so focus moves into these nodes are not treated as leaving
 * the editor during inline editing.
 */
const RICH_TEXT_FORMAT_TOOLBAR_SELECTORS = [
  '[data-rich-text-editor-ui="format-toolbar"]',
  '[data-rich-text-editor-ui="format-toolbar-submenu"]',
] as const;

const RICH_TEXT_EDITING_CONTINUATION_SELECTORS = [
  ...RICH_TEXT_FORMAT_TOOLBAR_SELECTORS,
  '[data-rich-text-editor-ui="block-toolbar-root"]',
  '[data-block-action-toolbar]',
] as const;

const RADIX_PORTAL_SELECTORS = [
  '[data-slot="popover-content"]',
  '[data-slot="dialog-content"]',
  '[data-slot="dialog-overlay"]',
  '[data-slot="dropdown-menu-content"]',
  '[data-slot="select-content"]',
] as const;

const EDITOR_PORTAL_SELECTORS = [
  ...RICH_TEXT_EDITING_CONTINUATION_SELECTORS,
  ...RADIX_PORTAL_SELECTORS,
] as const;

export function isRichTextEditingPortalTarget(target: EventTarget | null): boolean {
  const element = elementFromEventTarget(target);
  return (
    element !== null &&
    RICH_TEXT_FORMAT_TOOLBAR_SELECTORS.some((selector) => Boolean(element.closest(selector)))
  );
}

interface RichTextToolbarEngagementOptions {
  /** Sub-session active (e.g. AI menu) that keeps the toolbar open without editor focus. */
  sessionActive?: boolean;
  /**
   * Count editor focus as engaged. True for toolbar visibility (default).
   * False when checking whether to dismiss the AI menu on empty selection.
   */
  includeEditorFocus?: boolean;
}

/** Whether the user is still interacting with the rich text format toolbar. */
export function isRichTextToolbarEngaged(
  editor: { isFocused: boolean },
  options: RichTextToolbarEngagementOptions = {},
): boolean {
  if (options.sessionActive) return true;
  if ((options.includeEditorFocus ?? true) && editor.isFocused) return true;
  return isRichTextEditingPortalTarget(getDocument()?.activeElement ?? null);
}

/** Broad check for block-canvas selection / pointer handlers (includes all Radix overlays). */
export function isEditorPortalTarget(target: EventTarget | null): boolean {
  const element = elementFromEventTarget(target);
  return (
    element !== null &&
    EDITOR_PORTAL_SELECTORS.some((selector) => Boolean(element.closest(selector)))
  );
}

export function isOverlayPortalTarget(target: EventTarget | null): boolean {
  const element = elementFromEventTarget(target);
  return (
    element !== null &&
    RADIX_PORTAL_SELECTORS.some((selector) => Boolean(element.closest(selector)))
  );
}
